import Cookies from "js-cookie";
import { AiOutlineDelete } from "react-icons/ai";
import { MdClose } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Form } from "@/components/ui/form/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";

import { useCreateSalaryModal } from "./use-create-salary-modal";
import { Button } from "@/components/ui/button/button";
import {
  createSalaryByAdmin,
  generateSalaryComponentsFromDatesheet,
} from "@/config/mutation/admin";
import { Input } from "@/components/ui/input/input";
import { Label } from "@/components/ui/label/label";
import { fetchSingleTimesheetByIdForAdminQueryOptions } from "@/config/queries/query-options";

const formSchema = z.object({
  components: z.array(
    z.object({
      type: z.string(),
      name: z.string(),
      amount: z.coerce.number(),
    })
  ),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateSalaryModal = () => {
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const { open, onOpenChange, sheetId, setSheetId, isCreatedFromCsv } =
    useCreateSalaryModal();

  const { mutate: GetComponents, data: ComponentsData } = useMutation({
    mutationKey: ["sheet-components", sheetId],
    mutationFn: generateSalaryComponentsFromDatesheet(key),
  });

  const { data, isPending: isTimesheetLoading } = useQuery(
    fetchSingleTimesheetByIdForAdminQueryOptions(key, sheetId as string)
  );

  const csvCreatedComponents = useMemo(() => {
    if (isTimesheetLoading) {
      return [];
    }

    if (data === undefined || data?.sheet === undefined) {
      return [];
    }

    const result = data?.sheet?.components;

    return result;
  }, [data, isTimesheetLoading]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      components: [],
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createSalaryByAdmin(key),
  });

  function onSubmit(values: FormValues) {
    const components = values?.components;
    const EarningComponents = components?.filter((t) => t?.type === "Earning");
    const DeductionComponents = components?.filter(
      (t) => t?.type === "Deduction"
    );

    const totalEarnings = EarningComponents?.reduce((prev, current) => {
      return Number(current!?.amount) + prev;
    }, 0);

    const totalDeductions = DeductionComponents?.reduce((prev, current) => {
      return Number(current!?.amount) + prev;
    }, 0);

    const numEarnings = Number(totalEarnings?.toFixed(2));
    const numDeductions = Number(totalDeductions?.toFixed(2));

    const netPay = Number((numEarnings - numDeductions)?.toFixed(2));

    mutate(
      {
        sheet: sheetId as string,
        components,
        totalDeductions: numDeductions,
        totalEarnings: numEarnings,
        netPay,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["timesheets-month"],
          });

          toast.success("Salary Created");
          onOpenChange(false);

          form.reset();
        },

        onError: (error) => {
          console.log("ERROR WHILE creating salary", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  const {
    append: appendComponent,
    fields: Components,
    remove: removeComponent,
  } = useFieldArray({
    control: form.control,
    name: "components",
  });

  const realComponents = form.watch("components");

  useEffect(() => {
    if (isCreatedFromCsv) {
      if (data) {
        form.setValue("components", csvCreatedComponents);
      }
    } else {
      if (ComponentsData) {
        form.setValue("components", ComponentsData?.components);
      }
    }
  }, [ComponentsData, data, csvCreatedComponents]);

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-[1400px] p-0">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div>
                <div className="border-b px-3 py-2 flex items-center justify-between">
                  <h3 className="text-xl font-medium tracking-normal">
                    Create Salary
                  </h3>

                  <div className="relative flex items-center gap-4">
                    <Button type="submit" size="sm" disabled={isPending}>
                      Continue
                    </Button>

                    <button
                      type="button"
                      className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                      onClick={() => {
                        onOpenChange(false);
                        form.reset();
                        setSheetId(null);
                      }}
                    >
                      <MdClose className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-[0.3] p-5 space-y-4">
                    <h1 className="text-lg font-semibold underline underline-offset-4">
                      Details:
                    </h1>

                    <div className="flex items-center gap-3">
                      <span>Employee - {data?.sheet?.employee?.name}</span>
                    </div>

                    {(() => {
                      const EarningComponents = realComponents?.filter(
                        (t) => t?.type === "Earning"
                      );
                      const DeductionComponents = realComponents?.filter(
                        (t) => t?.type === "Deduction"
                      );

                      const totalEarnings = EarningComponents?.reduce(
                        (prev, current) => {
                          return Number(current!?.amount) + prev;
                        },
                        0
                      );

                      const totalDeductions = DeductionComponents?.reduce(
                        (prev, current) => {
                          return Number(current!?.amount) + prev;
                        },
                        0
                      );

                      const numEarnings = Number(totalEarnings?.toFixed(2));
                      const numDeductions = Number(totalDeductions?.toFixed(2));

                      const netPay = Number(
                        (numEarnings - numDeductions)?.toFixed(2)
                      );

                      return (
                        <div>
                          <div className="flex flex-col gap-2 border-b pb-1">
                            <div className="flex items-center">
                              Total Earnings - {totalEarnings}
                            </div>
                            <div className="flex items-center">
                              Total Deduction - {totalDeductions}
                            </div>
                            <div className="flex items-center">
                              Net Pay - {netPay}
                            </div>

                            <p>
                              Employee's Gross Salary -{" "}
                              {data?.sheet?.employee?.grossSalary}
                            </p>
                            <p className="text-sm font-medium">
                              <span className="text-red-500 mr-1">Note:</span>
                              Basic Components will be generated based on
                              employee's gross salary.
                            </p>
                          </div>

                          {data?.sheet && (
                            <div className="flex flex-col gap-2 mt-3">
                              <div className="flex items-center">
                                Salary Timesheet of - {data?.sheet?.month},{" "}
                                {data?.sheet?.year}
                              </div>
                              {!isCreatedFromCsv && (
                                <>
                                  <div className="flex items-center">
                                    Present - {data?.sheet?.present}
                                  </div>
                                  <div className="flex items-center">
                                    Absent - {data?.sheet?.absent}
                                  </div>
                                  <div className="flex items-center">
                                    Paid Leave - {data?.sheet?.paidLeave}
                                  </div>
                                  <div className="flex items-center">
                                    Unpaid Leave - {data?.sheet?.unpaidLeave}
                                  </div>
                                  <div className="flex items-center">
                                    Holidays - {data?.sheet?.holiday}
                                  </div>
                                  <div className="flex items-center">
                                    Half Days - {data?.sheet?.halfDay}
                                  </div>
                                  <div className="flex items-center">
                                    Days Payable -{" "}
                                    {data?.sheet?.present +
                                      data?.sheet?.paidLeave +
                                      data?.sheet?.holiday +
                                      data?.sheet?.halfDay / 2}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex-[0.7] p-5 space-y-3 border-l max-h-[600px] overflow-y-auto">
                    <h1 className="text-lg">Components</h1>

                    <div>
                      {Components?.length > 0 ? (
                        <div className="px-5 flex flex-col gap-3">
                          {Components?.map((comp, index) => {
                            return (
                              <div
                                key={comp?.id}
                                className="flex items-center gap-2 border-b pb-3"
                              >
                                <h4 className="mr-3">{index + 1}.</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  <div className="space-y-1">
                                    <Label htmlFor={`name_${index}`}>
                                      Name
                                    </Label>
                                    <Input
                                      id={`name_${index}`}
                                      placeholder="component name..."
                                      type="text"
                                      {...form.register(
                                        `components.${index}.name` as const
                                      )}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label htmlFor={`type_${index}`}>
                                      Type
                                    </Label>
                                    <Select
                                      onValueChange={(value) => {
                                        console.log(`${index}_type`, value);
                                        form.setValue(
                                          `components.${index}.type` as const,
                                          value
                                        );
                                      }}
                                      value={form.getValues(
                                        `components.${index}.type`
                                      )}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                      </SelectTrigger>

                                      <SelectContent>
                                        <SelectItem value="Earning">
                                          Earning
                                        </SelectItem>
                                        <SelectItem value="Deduction">
                                          Deduction
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label htmlFor={`amount_${index}`}>
                                      Amount
                                    </Label>
                                    <Input
                                      type="number"
                                      id={`amount_${index}`}
                                      placeholder="component amount..."
                                      {...form.register(
                                        `components.${index}.amount` as const
                                      )}
                                      step={0.01}
                                      onChange={(e) => {
                                        form.setValue(
                                          `components.${index}.amount`,
                                          Number(e.currentTarget?.value)
                                        );
                                      }}
                                    />
                                  </div>
                                </div>

                                <span
                                  className="cursor-pointer hover:scale-110 active:scale-95 transition-all self-end mb-3"
                                  onClick={() => {
                                    removeComponent(index);
                                  }}
                                >
                                  <AiOutlineDelete size={20} />
                                </span>
                              </div>
                            );
                          })}

                          <Button
                            size="sm"
                            type="button"
                            className="cursor-pointer self-start"
                            onClick={() => {
                              appendComponent({
                                amount: 0,
                                name: "",
                                type: "Earning",
                              });
                            }}
                          >
                            Add a Component
                          </Button>
                        </div>
                      ) : (
                        <div className="px-5 flex items-center justify-center flex-col gap-2">
                          <h3 className="text-lg">No Components</h3>

                          <div className="flex flex-row gap-3 items-center">
                            <Button
                              size="sm"
                              type="button"
                              className="cursor-pointer"
                              onClick={() => {
                                GetComponents({
                                  sheetId: sheetId as string,
                                });
                              }}
                            >
                              Generate basic components
                            </Button>
                            <p>OR</p>
                            <Button
                              size="sm"
                              type="button"
                              className="cursor-pointer"
                              onClick={() => {
                                appendComponent({
                                  amount: 0,
                                  name: "",
                                  type: "Earning",
                                });
                              }}
                            >
                              Add Component
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
